<script setup lang="ts" generic="T extends (NutsProductVariant | NutsProductProjectionVariant)">
import { money } from '@nuts/auto-delivery-sdk/dist/utils/format';
import { computed } from 'vue';

import Select from '@/components/base/form/OutlinedSelect.vue';
import VariantOption from '@/components/ppa/VariantOption.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import {
  findSmallestEligibleVariant,
  getUnitName,
  isIndividuallyWrapped,
  isSampleOrSingleServe,
  NutsProductVariant,
  priceForQuantity,
  PurchaseOptions,
} from '@/utils/product';
import { NutsProductProjectionVariant } from '@/utils/productProjection';

const { variantGroups, variants, isGiftCertificate } = defineProps<{
  masterVariantId: number;
  isGiftCertificate: boolean;
  showOutOfStock?: boolean;
  variantGroups?: T[];
  variants: T[];
}>();

const selections = defineModel<PurchaseOptions>({ required: true });

const { flags } = useFeatureFlags();

function isNutsProduct(
  variant: NutsProductVariant | NutsProductProjectionVariant,
): variant is NutsProductVariant {
  return 'otherVariations' in variant;
}

function formatUnitName(variant: NutsProductVariant | NutsProductProjectionVariant) {
  return isNutsProduct(variant) ? variant.unitName : getUnitName(variant.variantName);
}

const variantsToSelectFrom = computed(() => variantGroups ?? variants);

const selectedVariantGroup = computed(() => {
  const isSelected = (variations: { id: number; sku: string }) =>
    isGiftCertificate
      ? variations.id === selections.value.variation
      : variations.sku === selections.value.sku;
  return (
    variantsToSelectFrom.value.find(
      (g) =>
        isSelected(g) ||
        (isNutsProduct(g) && g.otherVariations?.some((o) => o.variations.find(isSelected))),
    ) ?? variantsToSelectFrom.value[0]
  );
});

const selectedVariant = computed(
  () =>
    variants.find((v) =>
      isGiftCertificate ? v.id === selections.value.variation : v.sku === selections.value.sku,
    ) ?? variants[0],
);

const variationsOptions = computed(() =>
  isNutsProduct(selectedVariantGroup.value)
    ? selectedVariantGroup.value.otherVariations?.map((variation) =>
        variation.variations.map((option) => ({
          label: option[variation.key]?.label.en ?? '',
          value: isGiftCertificate ? option.id : option.sku,
        })),
      ) ?? []
    : [],
);

const hasLongVariant = computed(() =>
  variantsToSelectFrom.value.some((v) => {
    const unitName = formatUnitName(v);
    return (unitName?.length || 0) > 23;
  }),
);

const showSavings = computed(
  () =>
    !variantsToSelectFrom.value.some((group) =>
      isNutsProduct(group)
        ? group.prices.some((price) => price.discounted) ||
          group.otherVariations?.some((o) =>
            o.variations.some((variant) => variant.prices.some((price) => price.discounted)),
          )
        : group.listPrice.discounted,
    ),
);

const smallestVariant = computed(() => findSmallestEligibleVariant(variantsToSelectFrom.value));

const sortedVariants = computed(() => {
  const variantsToSort = [...variantsToSelectFrom.value];

  return variantsToSort.sort((a, b) => {
    // If one is sample/single serve and the other isn't, put sample/single serve last
    if (isSampleOrSingleServe(a) && !isSampleOrSingleServe(b)) return 1;
    if (!isSampleOrSingleServe(a) && isSampleOrSingleServe(b)) return -1;

    // If both are the same type, sort by weight (smallest to largest)
    if (a.weight && b.weight) {
      return a.weight - b.weight;
    }

    return 0;
  });
});

const hasVariantLabel = computed(() => variantsToSelectFrom.value.every((v) => v.variantLabel));
</script>

<template>
  <div>
    <fieldset>
      <legend class="items-center text-xs sm:text-sm" data-test="gift-value">
        <strong>
          {{ isGiftCertificate ? 'Value:' : 'Size:' }}
        </strong>
        <span class="ml-1 text-neutral-600">
          {{
            isGiftCertificate
              ? money(selectedVariant.prices[0].value)
              : formatUnitName(selectedVariant)
          }}
        </span>
        <span
          v-if="!isSampleOrSingleServe(selectedVariant)"
          class="inline-flex ml-1 text-neutral-600"
        >
          (
          <strong>
            {{ money(priceForQuantity(selectedVariant, { quantity: selections.quantity }, true)) }}
          </strong>
          /oz)
        </span>
        <span
          v-if="!isIndividuallyWrapped(selectedVariant)"
          class="px-1 ml-2 text-black rounded bg-neutral-200"
        >
          Not individually packaged
        </span>
        <span v-if="showOutOfStock && selectedVariant.backordered" class="ml-1 text-nuts-red-800">
          Out of stock
        </span>
      </legend>
      <div v-if="isGiftCertificate" class="grid grid-cols-4 gap-2 mt-2">
        <VariantOption
          v-for="group in variantsToSelectFrom"
          v-model="selections.variation"
          :key="group.id"
          isGiftCertificate
          :isPrimary="false"
          :isSelected="group.id === selectedVariantGroup.id"
          :showSavings="false"
          :variant="group"
        />
      </div>
      <div
        v-else
        class="grid grid-cols-2 gap-2 mt-2"
        :class="{
          'xl:grid-cols-3': !hasLongVariant,
        }"
        data-test="variant-size-selector"
      >
        <VariantOption
          v-for="group in sortedVariants"
          v-model="selections.sku"
          :key="group.id"
          :showVariantLabel="flags.ppaVariantLabel && hasVariantLabel"
          :isPrimary="group.id === masterVariantId && sortedVariants.length > 1"
          :isSelected="group.id === selectedVariantGroup.id"
          :selections
          :showSavings
          :smallestVariant
          :variant="group"
        />
      </div>
    </fieldset>
    <template v-if="isNutsProduct(selectedVariantGroup)">
      <fieldset
        v-for="(variation, index) in selectedVariantGroup.otherVariations"
        class="mt-4"
        :key="variation.key"
      >
        <label
          v-if="isNutsProduct(selectedVariant)"
          :for="`variation-select-${variation.key}`"
          class="m-0"
          data-test="grind-size-label"
        >
          <legend class="text-xs sm:text-sm" data-test="grind-size-label">
            <strong> {{ variation.label }}: </strong>
            <span class="ml-1 font-normal text-neutral-600">
              {{ selectedVariant[variation.key]?.label.en }}
            </span>
          </legend>
          <Select
            v-model="selections.sku"
            class="mt-2 font-semibold rounded-md customization-select"
            data-test="grind-size-select"
            :id="`variation-select-${variation.key}`"
            :options="variationsOptions[index]"
          />
        </label>
      </fieldset>
    </template>
  </div>
</template>

<style scoped>
.customization-select {
  @apply bg-neutral-100 border-black;
}
</style>
